import { useState, useMemo } from 'react'
import { Modal } from '@/components/shared/Modal'
import { Button } from '@/components/shared/Button'
import { Spinner } from '@/components/shared/Spinner'
import { useDashboard } from '@/hooks/useDashboard'
import { useGiveUp } from '@/hooks/useGiveUp'
import { formatCurrency } from '@/utils/formatters'

interface GiveUpFunnelProps {
  open: boolean
  onClose: () => void
}

type Step = 'offer' | 'geo' | 'amount' | 'confirm'

/**
 * Guided give-up flow (Feature 1d). Affiliates don't hunt for a geo card:
 * Give Up Cap → choose offer → choose geo → choose amount → confirm. Rendered in
 * the shared dark-glass Modal (uses semantic tokens, which the Modal's .dark scope
 * flips to the dark palette). Picking an offer switches the dashboard's selected
 * offer, so min give-up = THAT offer's lowest per-conversion payout.
 */
export function GiveUpFunnel({ open, onClose }: GiveUpFunnelProps) {
  const { offers, selectedOfferId, selectOffer, allocations, tiers, capUnit, isLoading } = useDashboard()
  const { isLoading: submitting, error, submit, clearError } = useGiveUp()

  const [step, setStep] = useState<Step>('offer')
  const [geoId, setGeoId] = useState<number | null>(null)
  const [amountStr, setAmountStr] = useState('')

  const minGiveUp = useMemo(() => {
    const payouts = tiers.map(t => t.affiliate_payout || t.pub_payout).filter(v => v > 0)
    return capUnit === 'conversions' ? 1 : (payouts.length ? Math.min(...payouts) : 1)
  }, [tiers, capUnit])

  const giveableAllocs = allocations.filter(a => (a.cap_remaining ?? 0) > 0)
  const chosen = giveableAllocs.find(a => a.geo_id === geoId) ?? null
  const maxGiveable = chosen?.cap_remaining ?? 0
  const parsedAmount = Math.round(Number(amountStr) || 0)
  const amountValid = parsedAmount >= minGiveUp && parsedAmount <= maxGiveable

  const reset = () => { setStep('offer'); setGeoId(null); setAmountStr(''); clearError() }
  const close = () => { reset(); onClose() }

  const pickOffer = (id: number) => {
    if (id !== selectedOfferId) selectOffer(id)
    setGeoId(null)
    setStep('geo')
  }
  const pickGeo = (id: number) => { setGeoId(id); setStep('amount') }
  const doSubmit = async () => {
    if (geoId == null) return
    const ok = await submit(geoId, parsedAmount)
    if (ok) close()
  }

  const stepLabel = { offer: '1 of 3 · Offer', geo: '2 of 3 · Geo', amount: '3 of 3 · Amount', confirm: 'Confirm' }[step]
  const optionBtn =
    'w-full text-left px-4 py-3 rounded-xl border border-border-subtle bg-surface-elevated hover:border-brand-blue/70 hover:bg-surface-card transition-colors text-sm font-medium text-text-primary'

  return (
    <Modal open={open} onClose={close} title={`Give Up Cap — ${stepLabel}`}>
      {step === 'offer' && (
        <div className="space-y-3">
          <p className="text-sm text-text-tertiary">Which offer do you want to release cap from?</p>
          <div className="grid grid-cols-1 gap-2">
            {offers.map(o => (
              <button
                key={o.id}
                onClick={() => pickOffer(o.id)}
                disabled={!!o.is_paused}
                className={`${optionBtn} ${o.is_paused ? 'opacity-50 cursor-not-allowed hover:border-border-subtle hover:bg-surface-elevated' : ''}`}
              >
                {o.name}
                {o.is_paused ? (
                  <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-600">
                    Paused
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'geo' && (
        <div className="space-y-3">
          <p className="text-sm text-text-tertiary">Which geo? (only geos with remaining cap are shown)</p>
          {isLoading ? (
            <div className="py-6 flex justify-center"><Spinner className="w-6 h-6" /></div>
          ) : giveableAllocs.length === 0 ? (
            <p className="text-sm text-text-placeholder py-4">No geos with remaining cap for this offer.</p>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {giveableAllocs.map(a => (
                <button key={a.geo_id} onClick={() => pickGeo(a.geo_id)} className={`${optionBtn} flex items-center justify-between`}>
                  <span>{a.geo_name} ({a.geo_code})</span>
                  <span className="text-xs text-text-muted">{formatCurrency(a.cap_remaining ?? 0)} left</span>
                </button>
              ))}
            </div>
          )}
          <Button variant="secondary" onClick={() => setStep('offer')} className="text-xs">Back</Button>
        </div>
      )}

      {step === 'amount' && chosen && (
        <div className="space-y-4">
          <p className="text-sm text-text-tertiary">
            How much to release for <strong className="text-text-primary">{chosen.geo_code}</strong>?
          </p>
          <input
            type="number"
            value={amountStr}
            onChange={e => setAmountStr(e.target.value)}
            placeholder="e.g. 1000"
            min={minGiveUp}
            max={maxGiveable}
            className="w-full px-3 py-2.5 rounded-xl text-sm bg-surface-card border border-border-subtle text-text-primary placeholder:text-text-placeholder focus:outline-none focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/25"
          />
          <p className="text-xs text-text-muted">Min {formatCurrency(minGiveUp)} (one conversion) · Max {formatCurrency(maxGiveable)}</p>
          <div className="flex items-center gap-3 pt-1">
            <Button variant="secondary" onClick={() => setStep('geo')} className="flex-1">Back</Button>
            <Button variant="primary" disabled={!amountValid} onClick={() => setStep('confirm')} className="flex-1">Continue</Button>
          </div>
        </div>
      )}

      {step === 'confirm' && chosen && (
        <div className="space-y-4">
          <p className="text-sm text-text-tertiary">
            Release <strong className="text-text-primary">{formatCurrency(parsedAmount)}</strong> in{' '}
            <strong className="text-text-primary">{chosen.geo_code}</strong> to the overflow pool?
          </p>
          <p className="text-xs text-brand-orange font-medium">
            This releases cap for the <strong>remainder of today only</strong> — it returns to you at the 00:00 UTC reset.
          </p>
          {error && (
            <div className="p-3 rounded-lg bg-brand-red/15 border border-brand-red/30"><p className="text-xs text-brand-red">{error}</p></div>
          )}
          <div className="flex items-center gap-3 pt-1">
            <Button variant="secondary" onClick={() => setStep('amount')} disabled={submitting} className="flex-1">Back</Button>
            <Button variant="danger" onClick={doSubmit} disabled={submitting} className="flex-1">
              {submitting ? <><Spinner className="w-4 h-4" /> Processing...</> : 'Confirm Give Up'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
